import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence, useInView, useReducedMotion } from "framer-motion";
import api from "@/lib/api";
import { track } from "@/lib/analytics";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ScoreRing, ScoreBar, VerdictBadge } from "@/components/DonkeyBits";
import { ArrowRight, AlertTriangle, ArrowLeft } from "lucide-react";
import { PublicAmbient, KineticHeading, Reveal } from "@/components/public/PublicVisuals";
import { useCountUp } from "@/hooks/useCountUp";

// The verdict assembles as an event: score calculates, then the verdict text lands with a gold sweep.
function FinalVerdict({ score, text }) {
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const inView = useInView(ref, { amount: 0.5, once: true });
  const val = useCountUp(score, inView, { duration: 1300, reduce });
  return (
    <motion.div
      ref={ref}
      initial={reduce ? false : { opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.4 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      className="relative overflow-hidden rounded-2xl border-2 border-donkey-orange/50 bg-donkey-deep p-7 mt-8 shadow-[0_0_50px_rgba(249,115,22,0.15)]"
      data-testid="example-final-verdict"
    >
      {!reduce && (
        <div className="brand-lightsweep" aria-hidden />
      )}
      <div className="relative flex items-start gap-5">
        {typeof score === "number" && <ScoreRing value={val} size={84} />}
        <div>
          <p className="text-xs uppercase tracking-[0.15em] text-donkey-grey mb-1">Assembled from the evidence</p>
          <h3 className="font-heading font-semibold text-donkey-orange mb-2 text-lg">The Donkey's Verdict</h3>
          <p className="text-donkey-warm/90 leading-relaxed">{text}</p>
        </div>
      </div>
    </motion.div>
  );
}

// Cinematic lead-in: the analysis visibly runs before the report, then settles.
const PHASES = [
  { k: "Evidence", payload: "Market demand · competitor set · customer reach", tone: "green" },
  { k: "Challenge", payload: "“It'll sell itself” — rejected, no evidence", tone: "red" },
  { k: "Scoring", payload: "Weighing fit, demand, reach and risk…", tone: "amber" },
  { k: "Verdict", payload: "Assembling the Donkey's conclusion", tone: "orange" },
];
function AnalysisSequence() {
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.5 });
  const [p, setP] = useState(reduce ? PHASES.length : 0);
  useEffect(() => {
    if (reduce || !inView) return;
    const ts = [200, 1200, 2200, 3200, 4000].map((ms, i) => setTimeout(() => setP(i + 1), ms));
    return () => ts.forEach(clearTimeout);
  }, [inView, reduce]);
  const toneC = { green: "text-donkey-green", red: "text-donkey-red", amber: "text-donkey-amber", orange: "text-donkey-orange" };
  return (
    <div ref={ref} className="relative overflow-hidden rounded-2xl border border-white/10 bg-donkey-black/50 p-5 sm:p-6 my-8" data-testid="analysis-sequence">
      <p className="text-[11px] uppercase tracking-[0.2em] text-donkey-orange mb-4">Watching the analysis run</p>
      <div className="flex items-center gap-2 mb-4">
        {PHASES.map((ph, i) => (
          <div key={ph.k} className="flex-1 flex items-center gap-2">
            <motion.div className="flex-1 h-1 rounded-full bg-white/8 overflow-hidden">
              <motion.div className="h-full bg-gradient-to-r from-donkey-orange to-donkey-warmorange" initial={{ width: 0 }} animate={{ width: p > i ? "100%" : 0 }} transition={{ duration: 0.5 }} />
            </motion.div>
          </div>
        ))}
      </div>
      <div className="flex flex-wrap gap-2 mb-3">
        {PHASES.map((ph, i) => (
          <span key={ph.k} className={`text-[10px] uppercase tracking-wider rounded-full border px-2.5 py-1 transition-colors ${p > i ? "border-donkey-orange/50 bg-donkey-orange/10 text-donkey-warmorange" : "border-white/10 text-donkey-grey"}`}>{ph.k}</span>
        ))}
      </div>
      <div className="h-6">
        <AnimatePresence mode="wait">
          {p > 0 && p <= PHASES.length && (
            <motion.p key={p} initial={reduce ? false : { opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={reduce ? undefined : { opacity: 0, y: -6 }} transition={{ duration: 0.35 }}
              className={`text-sm ${toneC[PHASES[Math.min(p, PHASES.length) - 1].tone]}`}>
              {PHASES[Math.min(p, PHASES.length) - 1].payload}
            </motion.p>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

export default function ExampleVerdict() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [ex, setEx] = useState(null);
  const [disclaimer, setDisclaimer] = useState("");

  useEffect(() => {
    track("view_sample_verdict", { sample: id || "start" });
    api
      .get(`/preview/examples/${id || "start"}`)
      .then(({ data }) => { setEx(data.example); setDisclaimer(data.disclaimer); })
      .catch(() => navigate("/meet-the-donkey"));
  }, [id, navigate]);

  if (!ex) return <div className="min-h-[50vh]" />;

  return (
    <div className="relative overflow-hidden">
      <PublicAmbient intensity={0.6} />
      <div className="relative z-10 mx-auto max-w-4xl px-5 sm:px-8 py-12">
      <button onClick={() => navigate("/meet-the-donkey")} className="text-sm text-donkey-grey hover:text-donkey-warm flex items-center gap-1 mb-6" data-testid="back-to-meet">
        <ArrowLeft className="w-4 h-4" /> Back to Meet the Donkey
      </button>

      {/* Fictional banner */}
      <div className="flex items-start gap-3 rounded-xl border border-donkey-amber/40 bg-donkey-amber/5 p-4 mb-8" data-testid="fictional-banner">
        <AlertTriangle className="w-5 h-5 text-donkey-amber shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-semibold text-donkey-amber">Illustrative sample — not a real customer.</p>
          <p className="text-xs text-donkey-amber/80 mt-1">{disclaimer}</p>
        </div>
      </div>

      <p className="text-xs uppercase tracking-[0.2em] text-donkey-orange mb-2">Personal Opportunity Profile</p>
      <KineticHeading text={ex.persona} className="text-3xl sm:text-4xl" />
      <p className="text-xs uppercase tracking-[0.15em] text-donkey-grey mt-6 mb-1">What the Donkey heard</p>
      <p className="text-donkey-grey leading-relaxed">{ex.circumstances}</p>

      <AnalysisSequence />

      {/* Opportunities */}
      <h2 className="font-heading font-semibold text-xl text-donkey-warm mt-12 mb-5">Three opportunities</h2>
      <div className="space-y-5">
        {ex.opportunities.map((o, i) => (
          <Reveal key={i} y={30} delay={i * 0.08}>
          <div className="card-donkey rounded-2xl p-6 transition-all hover:border-donkey-orange/40" data-testid={`example-opp-${i}`}>
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-[11px] uppercase tracking-[0.15em] text-donkey-orange">{o.rank_label}</p>
                <h3 className="font-heading font-semibold text-lg text-donkey-warm mt-1">{o.name}</h3>
                <p className="text-sm text-donkey-grey mt-2">{o.proposition}</p>
              </div>
              <ScoreRing value={o.scores.overall} size={72} />
            </div>
            <Tabs defaultValue="overview" className="mt-5">
              <TabsList className="flex flex-wrap h-auto bg-donkey-bg border border-white/10 p-1 gap-1">
                {[["overview", "Overview"], ["numbers", "Tournament"], ["verdict", "Verdict"]].map(([v, l]) => (
                  <TabsTrigger key={v} value={v} className="text-xs data-[state=active]:bg-donkey-orange data-[state=active]:text-donkey-black text-donkey-grey">{l}</TabsTrigger>
                ))}
              </TabsList>
              <TabsContent value="overview" className="mt-4 space-y-2 text-sm text-donkey-warm/85">
                <p><span className="text-donkey-grey">Customer: </span>{o.customer}</p>
                <p><span className="text-donkey-green">Strongest evidence: </span>{o.strongest_evidence}</p>
                <p><span className="text-donkey-red">Largest weakness: </span>{o.largest_weakness}</p>
                {(o.trade_offs || []).map((t, k) => (
                  <p key={k}><span className="text-donkey-grey">Trade-off: </span>gain {t.gain}; accept {t.accept}</p>
                ))}
              </TabsContent>
              <TabsContent value="numbers" className="mt-4">
                {Object.entries(o.scores).filter(([k]) => k !== "overall").map(([k, v]) => (
                  <ScoreBar key={k} label={k.replace(/_/g, " ")} value={v} />
                ))}
              </TabsContent>
              <TabsContent value="verdict" className="mt-4">
                <VerdictBadge label={o.verdict_label} />
              </TabsContent>
            </Tabs>
          </div>
          </Reveal>
        ))}
      </div>

      {/* Tournament summary */}
      <h2 className="font-heading font-semibold text-xl text-donkey-warm mt-12 mb-4">Venture Tournament — who leads where</h2>
      <div className="card-donkey rounded-2xl overflow-hidden">
        {ex.tournament.map((t, i) => (
          <div key={i} className="flex items-center justify-between gap-4 px-5 py-3 border-b border-white/5 last:border-0 text-sm">
            <span className="text-donkey-grey">{t.dimension}</span>
            <span className="text-donkey-warm font-medium">{t.leader}</span>
            <span className="text-donkey-grey text-xs hidden sm:block flex-1 text-right">{t.note}</span>
          </div>
        ))}
      </div>

      {/* Adaptability + loss limit + evidence plan */}
      <div className="grid md:grid-cols-2 gap-5 mt-12">
        <div className="card-donkey rounded-2xl p-6">
          <h3 className="font-heading font-semibold text-donkey-warm mb-2">Adaptability</h3>
          <p className="text-sm text-donkey-grey">{ex.adaptability.summary}</p>
          <p className="text-xs text-donkey-orange mt-2">Resilience: {ex.adaptability.resilience}</p>
        </div>
        <div className="card-donkey rounded-2xl p-6">
          <h3 className="font-heading font-semibold text-donkey-warm mb-2">Minimum Loss Limit</h3>
          <p className="text-sm text-donkey-grey">Max money: <span className="text-donkey-warm font-mono">£{ex.minimum_loss_limit.max_money_gbp}</span> · Max hours: <span className="text-donkey-warm font-mono">{ex.minimum_loss_limit.max_hours}</span></p>
          <p className="text-sm text-donkey-grey mt-2"><span className="text-donkey-red">Abandon if: </span>{ex.minimum_loss_limit.abandon_if}</p>
        </div>
      </div>

      <div className="card-donkey rounded-2xl p-6 mt-5">
        <h3 className="font-heading font-semibold text-donkey-warm mb-3">First Evidence Plan</h3>
        {ex.first_evidence_plan.map((p, i) => (
          <div key={i} className="text-sm text-donkey-grey space-y-1">
            <p><span className="text-donkey-orange">Action: </span>{p.action}</p>
            <p><span className="text-donkey-grey">Tests: </span>{p.uncertainty_addressed}</p>
            <p><span className="text-donkey-green">Success signal: </span>{p.success_signal}</p>
          </div>
        ))}
      </div>

      {/* Final verdict — assembles as an event */}
      <FinalVerdict score={ex.opportunities?.[0]?.scores?.overall} text={ex.final_verdict} />

      <div className="mt-10 text-center">
        <p className="text-sm font-semibold text-donkey-amber mb-1">Illustrative sample — not a real customer.</p>
        <p className="text-xs text-donkey-amber/80 mb-4">{disclaimer}</p>
        <Button onClick={() => navigate("/pricing")} className="group relative bg-donkey-orange text-donkey-black font-semibold h-12 px-7" data-testid="example-cta">
          <span className="sheen-layer" aria-hidden />
          <span className="relative z-10 inline-flex items-center">Get your own Donkey's Verdict <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" /></span>
        </Button>
      </div>
      </div>
    </div>
  );
}
